import React, { useEffect, useState } from "react";

import { Col, Divider, Row, Space, Tooltip } from "antd";
import { ColumnsType } from "antd/lib/table";
import { FileTextOutlined } from "@ant-design/icons";
import { useAsyncAxios, utilAxiosExcelFileWithAuth, utilAxiosWithAuth } from "../utils/customAxios";
import { AlertType, useAlert } from "../provider/AlertProvider";
import { useNavigate } from "react-router-dom";

import * as Request from "../commons/request";
import * as Types from "../commons/typeDTO";
import * as TypeUtils from "../commons/typeUtils";
import * as String from "../commons/string";
import * as Common from "../commons/common";
import * as Utils from "../utils/utils";

import DataTable from "../components/DataTable";
import produce from "immer";
import MemberFilter from "../components/MemberFilter";
import qs from "qs";
import CryptoJS from "crypto-js";

function Users() {
    const alert = useAlert();
    const navigate = useNavigate();

    const [users, setUsers] = useState<Array<Types.UserDto>>();
    const [managers, setManagers] = useState<Array<Types.UserDto>>();
    const [currentUser, setCurrentUser] = useState<Types.UserDto>();
    const [userTableSize, setUserTableSize] = useState<number>(10);
    const [managerTableSize, setManagerTableSize] = useState<number>(10);
    const [requestUserPage, setRequestUserPage] = useState(0);
    const [tempUsers, setTempUsers] = useState<Array<Types.UserDto>>();

    const [editMode, setEditMode] = useState<string>("none");
    const [openDeleteUser, setOpenDeleteUser] = useState(false);

    const [filterUserValue, setFilterUserValue] = useState<TypeUtils.UserSearchType>({});
    const [filterManagerValue, setFilterManagerValue] = useState<TypeUtils.UserSearchType>({});

    const [isDownloading, setIsDownloading] = useState(false);

    const columns: ColumnsType<Types.UserDto> = [
        {
            align: "center",
            title: "번호",
            dataIndex: "num",
            width: "6%",
            render: (item, record, index) => (users && users.indexOf(record) + 1) || (managers && managers.indexOf(record) + 1),
        },
        {
            align: "center",
            title: "가입일",
            dataIndex: "createdDate",
            width: "10%",
            sorter: (a, b, sortOrder) => Utils.sortString(a.createdDate.valueOf(), b.createdDate.valueOf(), sortOrder),
            render: (_, row) => Utils.utcToLocalTime(row.createdDate.valueOf(), Common.FORMAT_SHORT_DATE_TIME_WITHOUT_SECOND),
        },
        {
            align: "center",
            title: "계정타입",
            dataIndex: "userType",
            width: "7%",
            sorter: (a, b, sortOrder) => Utils.sortString(a.userType, b.userType, sortOrder),
            render: (_, row) => (row.userType === "INDIVIDUAL" ? "개인" : "법인"),
        },
        {
            align: "center",
            title: String.userName,
            dataIndex: "name",
            width: "8%",
            sorter: (a, b, sortOrder) => Utils.sortString(a.name, b.name, sortOrder),
        },
        {
            align: "center",
            title: String.userEmail,
            dataIndex: "email",
            width: "10%",
            sorter: (a, b, sortOrder) => Utils.sortString(a.email, b.email, sortOrder),
        },
        {
            align: "center",
            title: String.userPhone,
            dataIndex: "phone",
            width: "15%",
            sorter: (a, b, sortOrder) => Utils.sortString(a.phone, b.phone, sortOrder),
            render: (_, row) => (row.phone ? row.phone.replace(/^(\d{2,3})(\d{3,4})(\d{4})$/, `$1-$2-$3`) : String.dash),
        },
        {
            align: "center",
            title: "사업자 등록번호",
            dataIndex: "companyRegistrationNo",
            width: "10%",
            sorter: (a, b, sortOrder) => Utils.sortString(a.companyRegistrationNo, b.companyRegistrationNo, sortOrder),
            render: (_, row) => (!row?.companyRegistrationNo ? String.dash : row.companyRegistrationNo),
        },
        {
            align: "center",
            title: "회사/단체",
            dataIndex: "b2bName",
            width: "10%",
            sorter: (a, b, sortOrder) => Utils.sortString(a.companyRegistrationNo, b.companyRegistrationNo, sortOrder),
            render: (_, row) => (row?.b2bs?.length ? row.b2bs.map((b2b) => b2b.b2bName).join(", ") : String.dash),
        },
        {
            align: "center",
            title: String.userStatus,
            dataIndex: "userStatus",
            width: "10%",
            sorter: (a, b, sortOrder) =>
                Utils.sortBoolean(
                    a.withdrawn || a.accountExpired || a.accountLocked || a.blocked,
                    b.withdrawn || b.accountExpired || b.accountLocked || b.blocked,
                    sortOrder
                ),
            render: (_, row) => {
                return row.withdrawn || row.accountExpired || row.accountLocked || row.blocked ? (
                    <Tooltip title={row.withdrawn ? "탈퇴" : row.accountExpired ? "휴면" : row.accountLocked ? "잠김" : row.blocked ? "블랙" : null}>
                        비활성화
                    </Tooltip>
                ) : (
                    "정상"
                );
            },
        },
        {
            align: "center",
            title: " ",
            dataIndex: "detail",
            render: (_, row) => {
                return (
                    <FileTextOutlined
                        onClick={() => {
                            navigate(Common.PAGE_LIST_USERS + "/" + encodeURIComponent(CryptoJS.AES.encrypt(row.email, "userEmail").toString()));
                        }}
                    />
                );
            },
        },
    ];

    const managerColumns = [...columns];
    managerColumns.splice(columns.length - 1, 0, {
        align: "center",
        title: "권한",
        dataIndex: "aurhority",
        width: "10%",
        sorter: (a, b, sortOrder) => Utils.sortString(Utils.convertAuthority(a.authority), Utils.convertAuthority(b.authority), sortOrder),
        render: (_, row) => {
            return Utils.convertAuthority(row.authority);
        },
    });

    const requestFilteredUserData = () => {
        console.log("requestUser", filterUserValue);
        requestGetUsers(["ROLE_USER"]);
    };

    //get user
    const requestAxiosGetUsers = async (authority?: string[]) => {
        const response = await utilAxiosWithAuth().get(Request.USERS_URL, {
            params: { authorityNames: authority, ...filterUserValue, page: requestUserPage, size: Common.USER_PAGE_SIZE },
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: "repeat" });
            },
        });
        return response.data;
    };

    const { loading: loadingGetUsers, error: errorGetUsers, data: resultGetUsers, execute: requestGetUsers } = useAsyncAxios(requestAxiosGetUsers);

    useEffect(() => {
        if (resultGetUsers === null) return;
        console.log("resultGetUsers", resultGetUsers);

        if (resultGetUsers.users.totalPages > requestUserPage + 1) {
            setTempUsers([...(tempUsers || []), ...resultGetUsers.users.content]);
            setRequestUserPage(requestUserPage + 1);
        } else {
            setUsers([...(tempUsers || []), ...resultGetUsers.users.content]);
            setRequestUserPage(0);
            setTempUsers([]);
        }
    }, [resultGetUsers]);

    useEffect(() => {
        if (errorGetUsers === null) return;

        //console.log("errorGetUsers", errorGetUsers);
        alert.setAlert(errorGetUsers.response.data.code, "Error", errorGetUsers.response.data.message);
    }, [errorGetUsers]);

    useEffect(() => {
        if (requestUserPage === 0) return;
        requestGetUsers();
    }, [requestUserPage]);

    // useEffect(() => {
    //     if (filterUserValue !== undefined) {
    //         requestFilteredUserData();
    //     }
    // }, [filterUserValue]);

    useEffect(() => {
        requestFilteredUserData();
    }, [filterUserValue]);

    //get manager
    const requestAxiosGetManagers = async (authority?: string[]) => {
        const response = await utilAxiosWithAuth().get(Request.USERS_URL, {
            params: { authorityNames: authority, ...filterManagerValue, size: Common.USER_PAGE_SIZE },
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: "repeat" });
            },
        });
        return response.data;
    };

    const {
        loading: loadingGetManagers,
        error: errorGetManagers,
        data: resultGetManagers,
        execute: requestGetManagers,
    } = useAsyncAxios(requestAxiosGetManagers);

    // useEffect(() => {
    //     if (filterManagerValue !== undefined) {
    //         requestFilteredManagerData();
    //     }
    // }, [filterManagerValue]);

    useEffect(() => {
        requestFilteredManagerData();
    }, [filterManagerValue]);

    const requestFilteredManagerData = () => {
        console.log("requestManager", filterManagerValue);
        requestGetManagers(["ROLE_ADMIN", "ROLE_MANAGER", "ROLE_RENT_MANAGER", "ROLE_B2B_MANAGER"]);
    };

    useEffect(() => {
        if (resultGetManagers === null) return;
        console.log("resultGetManagers", resultGetManagers);

        setManagers(resultGetManagers.users.content);
    }, [resultGetManagers]);

    useEffect(() => {
        if (errorGetManagers === null) return;

        //console.log("errorGetUsers", errorGetUsers);
        alert.setAlert(errorGetManagers.response.data.code, "Error", errorGetManagers.response.data.message);
    }, [errorGetManagers]);

    //remove user
    const requestAxiosRemoveUser = async (user: Types.UserDto) => {
        const response = await utilAxiosWithAuth().delete(Request.USER_URL + "/" + user?.email);
        return response.data;
    };

    const { loading: loadingRemoveUser, error: errorRemoveUser, data: resultRemoveUser, execute: requestRemoveUsers } = useAsyncAxios(requestAxiosRemoveUser);

    useEffect(() => {
        if (resultRemoveUser === null) return;
        console.log("resultRemoveUser", resultRemoveUser);

        alert.setAlert(AlertType.SUCCESS, "사용자 삭제 성공", "사용자 삭제를 완료하였습니다.");
        requestGetUsers();
    }, [resultRemoveUser]);

    useEffect(() => {
        if (errorRemoveUser === null) return;

        alert.setAlert(errorRemoveUser.response.data.code, "Error", errorRemoveUser.response.data.message);
    }, [errorRemoveUser]);

    const onRow = (value: Types.UserDto) => {
        setCurrentUser(value);
    };

    const onSearchUsers = (type: string, value: any) => {
        setFilterUserValue(
            produce((draft: any) => {
                draft[type as keyof TypeUtils.UserSearchType] = value;
            })
        );
    };

    const onSearchManagers = (type: string, value: any) => {
        setFilterManagerValue(
            produce((draft: any) => {
                draft[type as keyof TypeUtils.UserSearchType] = value;
            })
        );
    };

    // 리셋 기능
    const onClearUsers = () => setFilterUserValue({});

    const onClearManagers = () => setFilterManagerValue({});

    const handleDeleteUser = () => {
        if (currentUser !== undefined) setOpenDeleteUser(true);
    };

    const handleDeleteUserOk = () => {
        setOpenDeleteUser(false);
        requestRemoveUsers(currentUser);
    };

    const handleDeleteUserCancel = () => {
        setOpenDeleteUser(false);
    };

    const onRegisterUser = () => {
        requestGetUsers();
        setEditMode("none");
    };

    const onDownload = async (authority?: string[]) => {
        setIsDownloading(true);
        await utilAxiosExcelFileWithAuth()
            .get(Request.USERS_DOWNLOAD_URL, {
                params: { authorityNames: authority, ...filterUserValue },
                paramsSerializer: (params) => {
                    return qs.stringify(params, { arrayFormat: "repeat" });
                },
            })
            .then((response) => {
                console.log("response", response);

                const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers["content-type"] }));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", "회원목록.xlsx");
                document.body.appendChild(link);
                link.click();
            })
            .catch((error) => {
                console.log("error", error);
                alert.setAlert(error.code, "Error", error.message);
            })
            .finally(() => {
                setIsDownloading(false);
            });
    };

    return (
        <div className="container">
            <Space size={30} direction="vertical" style={{ width: "100%" }}>
                <h3 className="title">통합회원 관리</h3>
                <div>
                    <Row justify={"space-between"}>
                        <Col span={6}>
                            <Divider orientation="left">
                                <span>일반 회원정보</span>
                            </Divider>
                        </Col>
                    </Row>
                    <Space direction="vertical" size={20} style={{ width: "100%" }}>
                        <MemberFilter
                            filterValue={filterUserValue}
                            onSearch={onSearchUsers}
                            setTableSize={setUserTableSize}
                            request={requestFilteredUserData}
                            clear={onClearUsers}
                            onDownload={() => onDownload(["ROLE_USER"])}
                            isDownloading={isDownloading}
                        />
                        <DataTable
                            tableSize={userTableSize}
                            hasScroll={false}
                            columns={columns}
                            rowKey="email"
                            dataSource={users}
                            onRow={onRow}
                            loading={loadingGetUsers}
                        />
                    </Space>
                </div>
                <div>
                    <Row justify={"space-between"}>
                        <Col span={6}>
                            <Divider orientation="left">
                                <span>운영자 회원정보</span>
                            </Divider>
                        </Col>
                    </Row>
                    <Space direction="vertical" size={20} style={{ width: "100%" }}>
                        <MemberFilter
                            filterValue={filterManagerValue}
                            onSearch={onSearchManagers}
                            setTableSize={setManagerTableSize}
                            request={requestFilteredManagerData}
                            clear={onClearManagers}
                        />
                        <DataTable tableSize={managerTableSize} hasScroll={false} columns={managerColumns} rowKey="email" dataSource={managers} onRow={onRow} />
                    </Space>
                </div>
            </Space>
        </div>
    );
}

export default Users;
